import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { HABIT_STEPS, TRAIT_STEPS, EFFECT_STEP, LIKERT_LABELS, TOTAL_STEPS } from "../data/questions";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";

const STORAGE_KEY = (pid) => `ao_answers_${pid}`;

// Combined step list — 5 habits + 5 traits + 1 effect
const STEPS = [
  ...HABIT_STEPS.map((h) => ({ kind: "habit", ...h })),
  ...TRAIT_STEPS.map((t) => ({ kind: "trait", ...t })),
  { kind: "effect", ...EFFECT_STEP, title: "Effect Markers" },
];

function LikertRow({ item, value, onSelect, testPrefix }) {
  return (
    <div className="border-t border-[#1A1915]/15 pt-6">
      <div className="text-sm md:text-base text-[#1A1915] leading-relaxed">{item}</div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {[1, 2, 3, 4, 5].map((v) => (
          <button
            key={v}
            type="button"
            className={`likert-sq ${value === v ? "active" : ""}`}
            onClick={() => onSelect(v)}
            aria-label={`${LIKERT_LABELS[v - 1]} (${v})`}
            data-testid={`${testPrefix}-${v}`}
          >
            {v}
          </button>
        ))}
        <div className="text-xs text-[#78716C] ml-2 hidden md:block">
          {LIKERT_LABELS[0]} → {LIKERT_LABELS[4]}
        </div>
      </div>
    </div>
  );
}

export default function Assessment() {
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  // state: {habits: {Zone: {itemIdx: 1-5}}, traits: {Trait: {itemIdx: true/false}}, effect: {Marker: {itemIdx: 1-5}}}
  const [answers, setAnswers] = useState({ habits: {}, traits: {}, effect: {} });
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const check = async () => {
      try {
        await api.get(`/assessment/access/${purchaseId}`);
        setAllowed(true);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Access denied");
        setTimeout(() => navigate("/"), 1500);
      } finally {
        setChecking(false);
      }
    };
    check();
    const saved = localStorage.getItem(STORAGE_KEY(purchaseId));
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.answers) setAnswers(parsed.answers);
        if (typeof parsed.stepIdx === "number") setStepIdx(parsed.stepIdx);
      } catch (_) { /* ignore */ }
    }
  }, [purchaseId, navigate]);

  useEffect(() => {
    if (!allowed) return;
    localStorage.setItem(STORAGE_KEY(purchaseId), JSON.stringify({ answers, stepIdx }));
  }, [answers, stepIdx, purchaseId, allowed]);

  const step = STEPS[stepIdx];
  const progress = ((stepIdx + 1) / TOTAL_STEPS) * 100;

  // -------- helpers --------
  const setHabitValue = (zone, itemIdx, value) =>
    setAnswers((a) => ({ ...a, habits: { ...a.habits, [zone]: { ...(a.habits[zone] || {}), [itemIdx]: value } } }));
  const toggleTrait = (trait, itemIdx) =>
    setAnswers((a) => {
      const cur = a.traits[trait] || {};
      return { ...a, traits: { ...a.traits, [trait]: { ...cur, [itemIdx]: !cur[itemIdx] } } };
    });
  const setEffectValue = (marker, itemIdx, value) =>
    setAnswers((a) => ({ ...a, effect: { ...a.effect, [marker]: { ...(a.effect[marker] || {}), [itemIdx]: value } } }));

  // -------- validation --------
  const canProceed = useMemo(() => {
    if (!step) return false;
    if (step.kind === "habit") {
      const cur = answers.habits[step.zone] || {};
      return step.items.every((_, i) => typeof cur[i] === "number");
    }
    if (step.kind === "trait") {
      // multi is optional (spec: "leave the rest blank")
      return true;
    }
    if (step.kind === "effect") {
      // require all Likert answers in all markers
      return step.markers.every((m) => {
        const cur = answers.effect[m.marker] || {};
        return m.items.every((_, i) => typeof cur[i] === "number");
      });
    }
    return false;
  }, [step, answers]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  };
  const goNext = () => {
    if (!canProceed) { toast.error("Please answer every item on this step"); return; }
    if (stepIdx < STEPS.length - 1) {
      setStepIdx(stepIdx + 1);
      scrollToTop();
    }
  };
  const goBack = () => {
    if (stepIdx > 0) {
      setStepIdx(stepIdx - 1);
      scrollToTop();
    }
  };

  const handleSubmit = async () => {
    if (!canProceed) return;
    setSubmitting(true);
    // Build payload: habits → arrays, traits → checked_levels, effect → arrays
    const habits = {};
    HABIT_STEPS.forEach((h) => {
      const cur = answers.habits[h.zone] || {};
      habits[h.zone] = h.items.map((_, i) => cur[i] || 1);
    });
    const traits = {};
    TRAIT_STEPS.forEach((t) => {
      const cur = answers.traits[t.trait] || {};
      const checked_levels = t.items.reduce((acc, it, i) => (cur[i] ? [...acc, it.level] : acc), []);
      traits[t.trait] = { checked_levels };
    });
    const effect = {};
    EFFECT_STEP.markers.forEach((m) => {
      const cur = answers.effect[m.marker] || {};
      effect[m.marker] = m.items.map((_, i) => cur[i] || 1);
    });

    try {
      const { data } = await api.post("/assessment/submit", { purchase_id: purchaseId, habits, traits, effect });
      localStorage.removeItem(STORAGE_KEY(purchaseId));
      navigate(data.results_url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
      setSubmitting(false);
    }
  };

  const quickFill = () => {
    const habits = {};
    HABIT_STEPS.forEach((h) => {
      const obj = {};
      h.items.forEach((_, i) => (obj[i] = 3 + Math.floor(Math.random() * 3)));
      habits[h.zone] = obj;
    });
    const traits = {};
    TRAIT_STEPS.forEach((t) => {
      const obj = {};
      t.items.forEach((_, i) => { if (Math.random() > 0.5) obj[i] = true; });
      traits[t.trait] = obj;
    });
    const effect = {};
    EFFECT_STEP.markers.forEach((m) => {
      const obj = {};
      m.items.forEach((_, i) => (obj[i] = 3 + Math.floor(Math.random() * 3)));
      effect[m.marker] = obj;
    });
    setAnswers({ habits, traits, effect });
    toast.success("Filled all 11 steps");
  };

  if (checking) return <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="assessment-loading"><div className="eyebrow">Verifying access…</div></div>;
  if (!allowed) return null;

  // -------- render --------
  return (
    <div data-testid="assessment-page">
      <div className="top-progress" style={{ width: `${progress}%` }} data-testid="assessment-progress" />
      <div className="max-w-3xl mx-auto px-6 py-12 lg:py-16">
        <div className="flex items-center justify-between mb-8">
          <div className="eyebrow" data-testid="assessment-step-indicator">
            Step {String(stepIdx + 1).padStart(2, "0")} / {String(TOTAL_STEPS).padStart(2, "0")}
          </div>
          <div className="eyebrow text-[#B8863C]">
            {step.kind === "habit" && `Habit · ${step.zone}`}
            {step.kind === "trait" && `Trait · ${step.trait}`}
            {step.kind === "effect" && "Effect"}
          </div>
        </div>

        <h1 className="font-serif text-4xl lg:text-5xl text-[#1A1915] leading-tight" data-testid="assessment-question-title">
          {step.kind === "habit" && step.zone}
          {step.kind === "trait" && step.trait}
          {step.kind === "effect" && step.title}
        </h1>
        <p className="mt-4 text-sm text-[#78716C] leading-relaxed">
          {step.kind === "habit" && "Rate your honest agreement with each statement."}
          {step.kind === "trait" && "Check everything that applies to you. Leave the rest blank."}
          {step.kind === "effect" && "Rate each statement in every marker group."}
        </p>

        <div className="mt-10">
          {step.kind === "habit" && (
            <div className="space-y-8" data-testid="habit-block">
              {step.items.map((item, i) => (
                <LikertRow
                  key={i}
                  item={item}
                  value={(answers.habits[step.zone] || {})[i]}
                  onSelect={(v) => setHabitValue(step.zone, i, v)}
                  testPrefix={`habit-${step.step}-${i}`}
                />
              ))}
            </div>
          )}

          {step.kind === "trait" && (
            <div className="flex flex-col gap-2" data-testid="trait-block">
              {step.items.map((it, i) => {
                const active = !!(answers.traits[step.trait] || {})[i];
                return (
                  <button
                    key={i}
                    type="button"
                    className={`multi-tog text-left ${active ? "active" : ""}`}
                    onClick={() => toggleTrait(step.trait, i)}
                    data-testid={`trait-${step.step}-${i}`}
                  >
                    <span className={`inline-block w-3 h-3 mr-3 border ${active ? "bg-[#FAFAF9] border-[#FAFAF9]" : "border-[#1A1915]"}`} />
                    {it.text}
                  </button>
                );
              })}
            </div>
          )}

          {step.kind === "effect" && (
            <div className="space-y-12" data-testid="effect-block">
              {step.markers.map((m) => (
                <div key={m.marker}>
                  <div className="eyebrow text-[#B8863C]">Marker · {m.marker}</div>
                  <div className="mt-4 space-y-6">
                    {m.items.map((item, i) => (
                      <LikertRow
                        key={i}
                        item={item}
                        value={(answers.effect[m.marker] || {})[i]}
                        onSelect={(v) => setEffectValue(m.marker, i, v)}
                        testPrefix={`effect-${m.marker.replace(/\s+/g, '')}-${i}`}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-16 flex items-center justify-between border-t border-[#1A1915]/15 pt-8">
          <button className="btn-secondary" onClick={goBack} disabled={stepIdx === 0} data-testid="assessment-back-button">
            <ArrowLeft className="mr-2 w-4 h-4" strokeWidth={1.5} /> Back
          </button>
          <div className="flex items-center gap-3">
            {process.env.NODE_ENV === "development" && (
              <button className="text-xs uppercase tracking-widest text-[#78716C] hover:text-[#B8863C]" onClick={quickFill} data-testid="assessment-quickfill-button">
                Dev · quick-fill
              </button>
            )}
            {stepIdx < STEPS.length - 1 ? (
              <button className="btn-primary" onClick={goNext} disabled={!canProceed} data-testid="assessment-next-button">
                Next <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
              </button>
            ) : (
              <button className="btn-primary" onClick={handleSubmit} disabled={!canProceed || submitting} data-testid="assessment-submit-button">
                {submitting ? "Generating report…" : "Submit"}
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 text-xs text-[#78716C]">
          Answers save to this device only. Progress persists on refresh — cleared when you submit.
        </div>
      </div>
    </div>
  );
}
